import * as React from 'react';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import MailIcon from '@mui/icons-material/Mail';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import MoreIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../Context/ThemeContext';
import './Navbar.css'
import { DARKCOLORS, LIGHTCOLORS } from '../../../Utils/constants';
import { BedtimeSharp, WbSunnyOutlined } from '@mui/icons-material';
import Logo from '../../../assets/logo.svg'
import { getNotifications, getUserDetails } from '../../../Services/authService';
import { DropDownIcon, SidebarIcon } from '../CustomIcons';
import { CardMedia } from '@mui/material';
import TempProfileIcon from '../../../assets/ProfileIcon.webp'
import Profile from './Profile';
import NotificationsModal from './NotificationsModal';
import { Menu as LangMenu, MenuItem as LangMenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Mapping roles to display names
const roleMapping = {
  "ROLE_ADMIN": "Admin",
  "ROLE_SUPER_ADMIN": "Super Admin",
  "ROLE_EMPLOYEE": "Employee"
};

const languages = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
  { code: 'ch', label: 'Chinese' },
];

export default function Navbar({ toggleSidebar, isSidebarOpen }) {
  const { i18n, t } = useTranslation();
  const { mode, toggleTheme } = useThemeContext(); // Access theme context

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [loading, setLoading] = React.useState(null);
  const [userData, setUserData] = React.useState({});
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);
  const [showProfileModal, setShowProfileModal] = React.useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);

  const [langAnchorEl, setLangAnchorEl] = React.useState(null);
  const openLangMenu = Boolean(langAnchorEl);

  const handleLangMenuOpen = (event) => setLangAnchorEl(event.currentTarget);
  const handleLangMenuClose = () => setLangAnchorEl(null);

  const handleLanguageChange = (code) => {
    i18n.changeLanguage(code);
    handleLangMenuClose();
  };

  const fetchNotificationsCount = async () => {
    try {
      setLoading(true);
      const data = await getNotifications();
      // Count unread notifications
      const unread = data.filter(notif => notif.status === "UNREAD").length;
      setUnreadCount(unread);
    } catch (error) {
      console.error(t("navbar.errorFetchingNotifications"), error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNotificationsModal = () => {
    setShowNotificationsModal(true);
  };

  const handleCloseNotificationsModal = () => {
    setShowNotificationsModal(false);
  };

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const fetchCurrentUser = async () => {
     console.trace("fetchCurrentUser called");
    try {
      setLoading(true);
      const data = await getUserDetails();
      setUserData(data);
    } catch (error) {
      console.error(t("navbar.errorFetchingUserData"), error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
     console.log("Navbar mounted"); 
    fetchCurrentUser();
    fetchNotificationsCount();
  }, []);

  const handleProfileMenuOpen = () => {
    setShowProfileModal(true);
  };

  const handleCloseProfileModal = () => {
    setShowProfileModal(false);
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const navbarBackground = mode === 'light' ? LIGHTCOLORS.BackgroundDashboard : DARKCOLORS.BackgroundDashboard;
  const containerBackground = mode === 'light' ? LIGHTCOLORS.BackgroundCard : DARKCOLORS.BackgroundCard;
  const sidebarIconColor = mode === 'light' ? LIGHTCOLORS.GreyScale90 : DARKCOLORS.GreyScale90;
  const dropCircle = mode === 'dark' ? DARKCOLORS.GreyScale50 : LIGHTCOLORS.GreyScale50;
  const dropIcon = mode === 'dark' ? DARKCOLORS.GreyScale80 : LIGHTCOLORS.GreyScale80;
  const primaryColor = mode === 'dark' ? DARKCOLORS.Primary100 : LIGHTCOLORS.Primary100;

  const menuId = 'primary-search-account-menu';

  const mobileMenuId = 'primary-search-account-menu-mobile';
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem>
        <Box sx={{ display: 'flex' }}
          onClick={handleOpenNotificationsModal}

        >
          <IconButton
            size="large"
            aria-label="show 17 new notifications"
            color="inherit"
          // onClick={handleOpenNotificationsModal}

          >

            <Badge badgeContent={unreadCount} color="error">
              <NotificationsNoneOutlinedIcon />
            </Badge>
          </IconButton>
          <p>{t('navbar.Notifications')}</p>
        </Box>
      </MenuItem>
      <MenuItem onClick={handleProfileMenuOpen}>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit"
        >
          <AccountCircle />
        </IconButton>
        <p>{t('navbar.Profile')}</p>
      </MenuItem>
    </Menu>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <div className='appbar-sec-nav' style={{ backgroundColor: navbarBackground }}>
        <Toolbar>
          <Box className="navbar-left-sec"
            sx={{
              "@media (max-width: 1200px)": {
                display: "flex",
                gap: "40px",
              },
            }}>
            <img className='navbar-logo' src={Logo} alt="logo" />

            {/* Menu Icon */}
            <IconButton size="large" edge="start" color="inherit"
              sx={{
                marginLeft: isSidebarOpen ? "5rem" : "-2.5rem",
                '&:hover': {
                  backgroundColor: 'transparent', // Removes hover background color
                  color: 'inherit', // Keeps the text color unchanged
                },
                "@media (max-width: 1200px)": {
                  marginLeft: isSidebarOpen ? "3.5rem" : "-2.5rem",
                }
              }}
              onClick={toggleSidebar}>
              {isSidebarOpen ? <SidebarIcon color={sidebarIconColor} /> : <SidebarIcon style={{ transform: 'rotate(180deg)' }} color={sidebarIconColor} />}
            </IconButton>

            {/* Main Heading */}
            <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>
              <h1 className='main-heading-sec' style={{ margin: 0 }}>{t('dashboard.mainHeading')}</h1>
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />

          {/* Theme Toggle Button */}
          <IconButton sx={{
            background: containerBackground, '&:hover': {
              backgroundColor: containerBackground, // Ensures no background color on hover
            },
          }}
            size="medium" color="inherit" onClick={toggleTheme}>
            {mode === 'light' ? <BedtimeSharp /> : <WbSunnyOutlined />}
          </IconButton>

          {/* Desktop Menu Icons */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center', padding: 2 }}>
            {/* translations dropdown */}
            <Box
              onClick={handleLangMenuOpen}
              sx={{
                background: containerBackground,
                display: 'flex',
                alignItems: 'center',
                borderRadius: '24px',
                padding: '10px 12px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: containerBackground
                }
              }}
            >
              <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', marginRight: '4px' }}>
                {languages.find(lang => lang.code === i18n.language)?.label || 'Language'}
              </Typography>
              <DropDownIcon
                color1={dropCircle}
                color2={dropIcon}
                style={{
                  marginLeft: '4px',
                  transform: openLangMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                }}
              />
            </Box>

            <LangMenu
              anchorEl={langAnchorEl}
              open={openLangMenu}
              onClose={handleLangMenuClose}
              MenuListProps={{ 'aria-labelledby': 'language-button' }}
              sx={{
                '& .MuiPaper-root': {
                  borderRadius: '12px',
                  minWidth: 100,
                  padding: '4px 0'
                }
              }}
            >
              {languages.map(({ code, label }) => (
                <LangMenuItem
                  key={code}
                  selected={i18n.language === code}
                  onClick={() => handleLanguageChange(code)}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: primaryColor,
                    }
                  }}
                >
                  {label}
                </LangMenuItem>
              ))}
            </LangMenu>


            {/* Notifications Icon */}
            <IconButton sx={{
              background: containerBackground, '&:hover': {
                backgroundColor: containerBackground, // Ensures no background color on hover
              },
            }}
              onClick={handleOpenNotificationsModal}
              size="medium" aria-label="show 17 new notifications" color="inherit">
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsNoneOutlinedIcon />
              </Badge>
            </IconButton>

            {/* Profile Icon */}
            <IconButton
              sx={{
                background: containerBackground, '&:hover': {
                  backgroundColor: containerBackground, // Ensures no background color on hover
                },
                borderRadius: '48px', // Rounded corners for the IconButton
                padding: 0, display: "flex", alignItems: "center"
              }}
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >

              {/* Conditional rendering of profile image or icon */}
              <CardMedia
                component="img"
                image={userData?.profilePictureUrl || TempProfileIcon}
                sx={{
                  width: '41px',
                  height: '41px', // Stretch the image vertically
                  borderRadius: '50%', // Circular image
                  objectFit: 'cover',
                  marginLeft: '2px',
                }}
                title="Profile Image"
              />


              {/* Content Section */}
              <div className='profile-title'>
                <Typography variant="h6" component="div" sx={{ fontSize: '0.8rem', fontWeight: '700', padding: '2px 2px 0px' }}>
                  {userData?.firstName}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', letterSpacing: '0.5px', padding: '0px 2px 2px', fontWeight: '600' }}>
                  {roleMapping[userData?.authorities?.[1] || userData?.authorities?.[0]] || "Unknown Role"}
                </Typography>
              </div>
              <DropDownIcon color1={dropCircle} color2={dropIcon} style={{ marginRight: "10px" }} />
            </IconButton>
          </Box>
          {/* Mobile Menu Icon */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, borderRadius: '70px' }}>
            <IconButton
              size="large"
              aria-label="show more"
              aria-controls={mobileMenuId}
              aria-haspopup="true"
              onClick={handleMobileMenuOpen}
              color="inherit"
            >
              <MoreIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </div>

      {/* Render Menus */}
      {renderMobileMenu}

      <Profile open={showProfileModal} onClose={handleCloseProfileModal} profileData={userData} fetchCurrentUser={fetchCurrentUser} />
      <NotificationsModal open={showNotificationsModal} onClose={handleCloseNotificationsModal} onNotificationRead={fetchNotificationsCount} />

    </Box>
  );
}
